import { onMounted, onUnmounted } from 'vue';

import { bloomreachDebug } from '@/shared/lib/bloomreach/lib/debug';

/**
 * Allowed inbound trigger types from the Bloomreach script.
 * Distinct from BloomreachEventName (outbound tracking events).
 */
export type BloomreachEventType =
  | 'NPS_SURVEY'
  | (string & Record<never, never>);

/**
 * Flexible payload for campaign triggers (campaign_id, variant, metadata).
 */
export interface IBloomreachPayload {
  [key: string]: unknown;
}

/**
 * Normalized message delivered to subscribers (timestamp, type, payload).
 */
export interface IBloomreachMessage {
  type: BloomreachEventType;
  payload: IBloomreachPayload;
  timestamp: number;
}

/**
 * Single event name for all Bloomreach campaign triggers.
 * The external script must dispatch CustomEvents with this name;
 * the broker listens to it and fans out to Vue subscribers.
 */
export const BLOOMREACH_SIGNAL_EVENT = 'bloomreach:signal';

type Subscriber = (message: IBloomreachMessage) => void;

function normalizeDetail(detail: unknown): {
  type: BloomreachEventType;
  payload: IBloomreachPayload;
} {
  if (!detail || typeof detail !== 'object') {
    return { type: '', payload: {} };
  }
  const d = detail as Record<string, unknown>;
  const type = (d.type as BloomreachEventType) ?? '';
  const { type: _t, ...rest } = d;
  return { type, payload: rest as IBloomreachPayload };
}

class BloomreachTriggerBroker {
  private readonly subscribers = new Set<Subscriber>();

  private initialized = false;

  init(): void {
    if (typeof window === 'undefined' || this.initialized) {
      return;
    }
    this.initialized = true;
    window.addEventListener(
      BLOOMREACH_SIGNAL_EVENT,
      this.handleEvent as EventListener,
    );
  }

  private readonly handleEvent = (event: Event): void => {
    const customEvent = event as CustomEvent<unknown>;
    const { type, payload } = normalizeDetail(customEvent.detail);
    const message: IBloomreachMessage = {
      type,
      payload,
      timestamp: Date.now(),
    };
    this.subscribers.forEach(cb => {
      try {
        cb(message);
      } catch (err) {
        bloomreachDebug('[BloomreachTriggerBroker] subscriber error:', err);
      }
    });
  };

  subscribe(callback: Subscriber): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }
}

let instance: BloomreachTriggerBroker | null = null;

export function getBloomreachTriggerBroker(): BloomreachTriggerBroker {
  if (!instance) {
    instance = new BloomreachTriggerBroker();
  }
  return instance;
}

export function initBloomreachTriggerBroker(): void {
  getBloomreachTriggerBroker().init();
}

export interface UseBloomreachTriggersOptions {
  eventType?: BloomreachEventType;
}

/**
 * Subscribe to Bloomreach trigger events. Teardown runs on unmount to avoid leaks.
 */
export function useBloomreachTrigger(
  callback: (message: IBloomreachMessage) => void,
  options?: UseBloomreachTriggersOptions,
): void {
  const broker = getBloomreachTriggerBroker();
  const eventType = options?.eventType;

  let teardown: (() => void) | null = null;

  onMounted(() => {
    const handler = (message: IBloomreachMessage): void => {
      if (eventType !== undefined && message.type !== eventType) {
        return;
      }
      callback(message);
    };
    teardown = broker.subscribe(handler);
  });

  onUnmounted(() => {
    if (teardown) {
      teardown();
      teardown = null;
    }
  });
}
